import React, { useState, useContext, useEffect } from 'react';
import Styled from 'styled';
import { Link, useHistory } from 'react-router-dom';
import { __RouterContext } from 'react-router';

import SVGIcon from 'lib/images/svg-icon/svg-icon';
import { keyEnterPressed } from 'lib/key-code/key-code';

import arrowLeft from 'four-nets/icons/svg/icon_arrow-left.svg';
import arrowRight from 'four-nets/icons/svg/icon_arrow-right.svg';

import defaultStyles from './page-number-pager.scss';

interface Props {
    styles: typeof defaultStyles;
    currentPage: number;
    pageCount: number;
}

interface LinkProps {
    styles: typeof defaultStyles;
    page: number | null;
    children: React.ReactNode;
}

const PageNumberPager: React.FC<Props> = props => {
    const { styles } = props;
    const [value, setValue] = useState(props.currentPage);
    const routerContext = useContext(__RouterContext);
    const history = useHistory();

    useEffect(() => {
        if (value !== props.currentPage) {
            setValue(props.currentPage);
        }
    }, [props.currentPage]);

    const searchFromPage = (page: number) => {
        if (page === 1) {
            return '';
        }
        return `?page=${page}`;
    };

    const PageLink = Styled(defaultStyles)((props: LinkProps) => {
        if (!props.page) {
            return <span className={props.styles.pagerElementDisabled}>{props.children}</span>;
        }
        const link = `${routerContext.location.pathname}${searchFromPage(props.page)}`;
        return (
            <Link className={props.styles.pagerElement} to={link}>
                {props.children}
            </Link>
        );
    });

    const onInputFocus = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.target.select();
    };

    const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setValue(parseInt(event.target.value, 10));
    };

    const onKeyUp = (event: React.KeyboardEvent) => {
        if (keyEnterPressed(event)) {
            history.push(`${routerContext.location.pathname}${searchFromPage(value)}`);
        }
    };

    return (
        <nav>
            <ul className={styles.items}>
                <li>
                    <PageLink page={props.currentPage > 1 ? props.currentPage - 1 : null}>
                        <SVGIcon style={{ image: styles.icon }} src={arrowLeft} />
                    </PageLink>
                </li>
                {props.currentPage !== 1 ? (
                    <li className={styles.firstPage}>
                        <PageLink page={1}>1</PageLink>
                    </li>
                ) : null}
                <li className={styles.currentPage}>
                    <input
                        min={1}
                        max={props.pageCount}
                        type='number'
                        className={styles.inputPagerElement}
                        value={value}
                        onFocus={onInputFocus}
                        onChange={onInputChange}
                        onKeyUp={onKeyUp}
                    />
                </li>
                <li className={styles.of}>z</li>
                <li className={styles.pageCount}>
                    <PageLink page={props.pageCount}>{props.pageCount}</PageLink>
                </li>
                <li>
                    <PageLink page={props.currentPage < props.pageCount ? props.currentPage + 1 : null}>
                        <SVGIcon style={{ image: styles.icon }} src={arrowRight} />
                    </PageLink>
                </li>
            </ul>
        </nav>
    );
};
export default Styled(defaultStyles)(PageNumberPager);
